import React, { useState, useEffect } from "react";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";

const InventoryTable = ({
  inventoryData,
  fetchInventory,
  isLoading,
  currentPage,
  setCurrentPage,
  inventoryPerPage,
  setInventoryPerPage,
  totalItems,
  totalPages,
}) => {
  const TABLE_HEADERS = [
    "Action",
    "Selection",
    "SKU",
    "Name",
    "Stock",
    "Category",
    "Location",
    "Cost Price",
    "Selling Price",
    "Status",
  ];
  const [selectedItems, setSelectedItems] = useState([]);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);

  useEffect(() => {
    fetchInventory(); // Fetch inventory on mount
  }, []);

  // Fetch inventory as soon as something is typed
  // useEffect(() => {
  //   fetchInventory(); // Fetch inventory on mount
  // }, [fetchInventory]);

  const currentInventory = inventoryData;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };
  // Handle "select all" checkbox functionality
  const handleSelectAll = (checked) => {
    setSelectedItems(checked ? formattedInventory.map((s) => s["SKU"]) : []);
  };

  const handleActionClick = (itemSku, e) => {
    e.stopPropagation();
    setOpenDropdownRow((prev) => (prev === itemSku ? null : itemSku));
  };

  // Handle selecting individual checkboxes
  const handleSingleCheckbox = (itemSku) => {
    setSelectedItems((prev) =>
      prev.includes(itemSku)
        ? prev.filter((num) => num !== itemSku)
        : [...prev, itemSku]
    );
  };

  const handleEditItem = (itemSKU) => {
        // Implement edit logic
  };

  const deleteItem = async (itemSKU) => {
        // Implement edit logic
  };

  const renderActionColumn = (row) => (
    <td
      className={`sticky left-[80px] bg-white border-b border-gray-200 text-center ${
        openDropdownRow === row["SKU"] ? "z-[20]" : "z-[10]"
      }`}
      style={{
        width: "50px",
        minWidth: "50px",
        maxWidth: "50px",
        boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
      }}
    >
      <div className="relative">
        <button
          className="p-1 text-gray-500 hover:text-gray-800 relative w-full z-[1]"
          onClick={(e) => handleActionClick(row["SKU"], e)}
        >
          <MoreHorizontal size={18} className="mx-auto" />
        </button>

        {openDropdownRow === row["SKU"] && (
          <ActionMenu
            rowId={row["SKU"]}
            isOpen={openDropdownRow === row["SKU"]}
            onClose={() => setOpenDropdownRow(null)}
            moduleName="Inventory"
            actions={[
              {
                icon: "Eye",
                label: "View",
                color: "blue",
                action: (id) => {},
              },
              {
                icon: "Edit",
                label: "Edit",
                color: "green",
                action: (id) => handleEditItem(id),
              },
              {
                icon: "Trash",
                label: "Delete",
                color: "red",
                action: (id) => deleteItem(id),
              },
            ]}
          />
        )}
      </div>
    </td>
  );

  const formattedInventory = currentInventory.map((item) => ({
    SKU: item.sku,
    Name: item.productName,
    Stock: item.quantity,
    Category: item.productCategory,
    Location: item.productLocation,
    "Cost Price": `$${item.costPrice.toFixed(2)}`,
    "Selling Price": `$${item.sellingPrice.toFixed(2)}`,
    Status: (
      <span
        className={`py-1 px-3 rounded-full text-xs font-medium ${
          item.status
            ? "bg-green-200 text-green-800" // Changed from blue to green
            : "bg-gray-200 text-gray-800" // Changed from red to gray
        }`}
      >
        {item.status ? "Active" : "Inactive"}
      </span>
    ),
  }));

  return (
    <div className="bg-white flex flex-col h-full">
      {isLoading ? (
        <Loader
          color="text-blue-600"
          size="h-5 w-5"
          message="Loading Inventory..."
        />
      ) : (
        <>
          {/* 🔹 Inventory Table Display */}
          <div className="flex-1 min-h-[200px] sm:min-h-[300px] overflow-x-auto">
            <Table
              headers={TABLE_HEADERS}
              data={formattedInventory}
              onSelectAll={handleSelectAll}
            >
              {(row) => (
                <>
                  <td
                    className="sticky left-0 bg-white border-b border-gray-200"
                    style={{
                      width: "80px",
                      minWidth: "80px",
                      maxWidth: "80px",
                      boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="cursor-pointer mx-auto block"
                      checked={selectedItems.includes(row["SKU"] || "")}
                      onChange={() => handleSingleCheckbox(row["SKU"])}
                    />
                  </td>

                  {renderActionColumn(row)}

                  {TABLE_HEADERS.slice(2).map((header) => (
                    <td
                      key={header}
                      className="px-2 sm:px-3 py-1 sm:py-2 text-black text-xs sm:text-sm md:text-base border-b border-gray-200"
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "200px",
                      }}
                    >
                      {row[header]}
                    </td>
                  ))}
                </>
              )}
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between border-t items-center bg-white p-fluid-1">
          <div className="flex items-center">
              <span className="mr-2">Items per page</span>
              <select
                value={inventoryPerPage}
                onChange={(e) => {
                  setInventoryPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border-b-2 border-black focus:outline-none"
              >
                {[1, 5, 10, 25, 60].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="mx-2 text-gray-500 disabled:opacity-50"
              >
                ❮
              </button>
              <span>
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="mx-2 text-gray-500 disabled:opacity-50"
              >
                ❯
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default InventoryTable;
